import { useContext, useEffect, useState } from 'react';
import { MatrixType, ProjectType } from '../../../types/types';
import { Button, List, Modal, Tooltip } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { EyeOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import { cloneMatrix, deleteMatrix, getAllMatricesByProjectId } from '../../../features/projects/matrixSlices';
import { NotificationContext } from '../../../providers/NotificationProvider';
import { useDispatch } from 'react-redux';
import { formatDate } from '../../../features/utils/utils';
import { useTranslation } from 'react-i18next';

const MatrixList: React.FC<{ project_id: string; activeMatrixId?: number | null; refreshKey?: number }> = ({
  project_id,
  activeMatrixId,
  refreshKey,
}) => {
  const { t } = useTranslation();
  const normalizedActiveMatrixId =
    activeMatrixId === null || activeMatrixId === undefined ? null : Number(activeMatrixId);
  const [matrixes, setMatrixes] = useState<MatrixType[]>();
  const [cloningMatrixId, setCloningMatrixId] = useState<number | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { triggerNotification } = useContext(NotificationContext);
  useEffect(() => {
    const parsedProjectId = parseInt(project_id as string, 10);
    if (!project_id || isNaN(parsedProjectId)) {
      return;
    }
    dispatch(getAllMatricesByProjectId(parsedProjectId) as any)
      .then((result: any) => {
        if (getAllMatricesByProjectId.fulfilled.match(result)) {
          setMatrixes(result.payload);
        } else {
          triggerNotification('error', t('Error'), t('Error al cargar las matrices'), 'bottomRight');
        }
      })
      .catch(() => {
        triggerNotification('error', t('Error'), t('Error al cargar las matrices'), 'bottomRight');
      });
  }, [dispatch, project_id, refreshKey, triggerNotification]);

  const deleteMat = (id: MatrixType, project_id: string) => {
    if (!id || !id.id || isNaN(id.id)) {
      triggerNotification('error', t('Error'), t('ID de matriz no válido'), 'bottomRight');
      return;
    }

    if (!project_id || isNaN(parseInt(project_id))) {
      triggerNotification('error', t('Error'), t('ID de proyecto no válido'), 'bottomRight');
      return;
    }

    const project: Partial<ProjectType> = {
      id: parseInt(project_id),
    };

    const matrixToDelete: Partial<MatrixType> = {
      id: id.id,
      project: project,
      name: id.name,
      created_at: id.created_at,
      created_by: id.created_by,
    };
    dispatch(deleteMatrix(matrixToDelete) as any).then((result: any) => {
      if (deleteMatrix.fulfilled.match(result)) {
        triggerNotification('success', t('Éxito'), t('Matriz eliminada correctamente'), 'bottomRight');
        setMatrixes((prev) => {
          const updated = prev?.filter((matrix) => matrix.id !== id.id) ?? [];
          const isActive = normalizedActiveMatrixId !== null && normalizedActiveMatrixId === Number(id.id);
          if (isActive) {
            const nextMatrix = updated[0];
            if (nextMatrix?.id) {
              navigate(`/proyecto/${project_id}/matriz/${nextMatrix.id}/ver`);
            } else {
              navigate(`/proyecto/${project_id}/matriz/nueva`);
            }
          }
          return updated;
        });
      } else {
        triggerNotification('error', t('Error'), t('Error al eliminar la matriz'), 'bottomRight');
      }
    });
  };

  const confirmDeleteMatrix = (matrix: MatrixType) => {
    Modal.confirm({
      title: t('Eliminar matriz'),
      content: t('¿Deseas eliminar la matriz "{{name}}"? Esta acción no se puede deshacer.', { name: matrix.name }),
      okText: t('Eliminar'),
      cancelText: t('Cancelar'),
      okButtonProps: { danger: true },
      centered: true,
      onOk: () => deleteMat(matrix, project_id),
    });
  };

  const cloneMat = (matrix: MatrixType) => {
    if (!matrix?.id || isNaN(matrix.id)) {
      triggerNotification('error', t('Error'), t('ID de matriz no válido'), 'bottomRight');
      return;
    }

    const parsedProjectId = parseInt(project_id, 10);
    if (!project_id || isNaN(parsedProjectId)) {
      triggerNotification('error', t('Error'), t('ID de proyecto no válido'), 'bottomRight');
      return;
    }

    setCloningMatrixId(matrix.id);
    dispatch(cloneMatrix({ projectId: parsedProjectId, matrixId: matrix.id }) as any)
      .then((result: any) => {
        setCloningMatrixId(null);
        if (cloneMatrix.fulfilled.match(result)) {
          const clonedMatrix = result.payload as MatrixType;
          triggerNotification('success', t('Éxito'), t('Matriz clonada correctamente'), 'bottomRight');
          setMatrixes((prev) => {
            if (!prev) return [clonedMatrix];
            const exists = prev.some((item) => item.id === clonedMatrix.id);
            if (exists) {
              return prev.map((item) => (item.id === clonedMatrix.id ? clonedMatrix : item));
            }
            return [...prev, clonedMatrix];
          });
          dispatch(getAllMatricesByProjectId(parsedProjectId) as any)
            .then((refresh: any) => {
              if (getAllMatricesByProjectId.fulfilled.match(refresh)) {
                setMatrixes(refresh.payload);
              }
            })
            .catch(() => {
              // No bloquear al usuario si falla el refresh.
            });
        } else {
          triggerNotification('error', t('Error'), t('No se pudo clonar la matriz'), 'bottomRight');
        }
      })
      .catch(() => {
        setCloningMatrixId(null);
        triggerNotification('error', t('Error'), t('No se pudo clonar la matriz'), 'bottomRight');
      });
  };

  return (
    <>
      <div className='title-full'>
        <h1>{t('Matrices')}</h1>
        <Button type='primary' onClick={() => navigate(`/proyecto/${project_id}/matriz/nueva`)}>
          {t('Agregar Matriz')}
        </Button>
      </div>
      <div className='ant-divider'></div>
      <List
        dataSource={matrixes}
        renderItem={(item) => (
          <List.Item
            key={item.id}
            style={
              item.id === activeMatrixId
                ? {
                    background: '#f5faff',
                    borderRadius: 8,
                  }
                : undefined
            }
          >
            <List.Item.Meta
              title={<Link to={`/proyecto/${project_id}/matriz/${item.id}/ver`}>{item.name}</Link>}
              description={`${formatDate(new Date(item.created_at))} - ${item.created_by?.username}`}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <Tooltip title={t('Ver matriz')}>
                <Link
                  to={`/proyecto/${project_id}/matriz/${item.id}/ver`}
                  style={{ display: 'flex', padding: '4px 15px' }}
                >
                  <EyeOutlined />
                </Link>
              </Tooltip>
              <Tooltip title={t('Clonar matriz')}>
                <Button
                  type='link'
                  onClick={() => cloneMat(item)}
                  loading={cloningMatrixId === item.id}
                  disabled={cloningMatrixId === item.id}
                >
                  <CopyOutlined />
                </Button>
              </Tooltip>
              <Tooltip title={t('Eliminar matriz')}>
                <Button type='link' onClick={() => confirmDeleteMatrix(item)}>
                  <DeleteOutlined />
                </Button>
              </Tooltip>
            </div>
          </List.Item>
        )}
      />
    </>
  );
};

export default MatrixList;
